import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useAppState from '../../../AppStore';
import useDocumentDownload from '../api/useDocumentDownload';
import './identifyLayerInfoResultsLink.css';

const isDownloadLink = (url: string) =>
  url.startsWith('https://www.ag.ch/geoportal/api/v1/mapservices/documents?file=');

const isJsEventLink = (url: string) => url.startsWith('jsevent:');

const isFileSource = (url: string): boolean => {
  try {
    const { protocol } = new URL(url);
    return !['http:', 'https:'].includes(protocol);
  } catch {
    return true;
  }
};

interface ResultsLinkProps {
  link: string;
  description: string;
  onLinkWithJSEventClicked?: () => void;
}

const ResultsLink = ({
  link,
  description,
  onLinkWithJSEventClicked,
}: ResultsLinkProps) => {
  const { t } = useTranslation('agisviewer');
  const addNotification = useAppState((state) => state.addNotification);

  const { downloadFile, isLoading, error, isError } = useDocumentDownload();

  useEffect(() => {
    if (!isError || !error) return;

    const messageKey =
      error.message === '403' || error.message === 'Failed to fetch token'
        ? 'notification_identify_download_file_token_error'
        : 'notification_identify_download_file_default_error';

    addNotification({
      id: Date.now().toString(),
      title: t('notification_identify_download_file_error_title'),
      message: t(messageKey),
      type: 'negative',
    });
  }, [isError, error]);

  const handleUrlClicked = async (href: string) => {
    try {
      await navigator.clipboard.writeText(`explorer.exe ${href}`);
      addNotification({
        id: Date.now().toString(),
        title: t('notification_identify_internal_file_title'),
        message: t('notification_identify_internal_file'),
        type: 'information',
      });
    } catch {
      addNotification({
        id: Date.now().toString(),
        title: t('notification_identify_internal_file_error_title'),
        message: t('notification_identify_internal_file_error'),
        type: 'negative',
      });
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (isDownloadLink(link)) {
      downloadFile(link);
      return;
    }

    if (isJsEventLink(link)) {
      onLinkWithJSEventClicked?.();
      return;
    }

    if (isFileSource(link)) {
      let href = decodeURI(link).replace(/^file:\/\/\/?/, '');
      if (!href.startsWith('ulfitem')) href = href.replace(/\//g, '\\');
      handleUrlClicked(href);
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  return (
    <a
      href={link}
      onClick={handleClick}
      className={classNames('link', { disabledLink: isLoading })}
      title={link}
    >
      <span className="link__text">
        {isLoading ? `${description} (wird heruntergeladen)` : description}
      </span>
    </a>
  );
};

export default ResultsLink;
